import { Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BaseFormComponent } from '@classes/extends';
import { InputGala } from '@composants/index';
import { Constantes } from '../../services/util/constantes';

import { MatDialog } from '@angular/material/dialog';
import { HttpResponse } from '@angular/common/http';
import { AuthService as AuthentificationService, OrganismeConnexion, Utilisateur } from '@estel-numerique/galaclick-api';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { MdpOublieComponent } from './mdp-oublie/mdp-oublie.component';
import { MatCard } from '@angular/material/card';
import { InputGala as InputGala_1 } from '../composants/inputs/input.component';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { KeycloakAuthService } from '../../services/keycloak-auth.service';

@Component({
    selector: 'app-auth',
    templateUrl: './auth.component.html',
    styleUrls: ['./auth.component.css'],
    imports: [FormsModule, ReactiveFormsModule, MatCard, InputGala_1, MatButton, MatProgressSpinner]
})
export class AuthComponent extends BaseFormComponent implements OnInit {
    public wsAuth = inject(AuthentificationService);
    private formBuilder = inject(FormBuilder);
    private dialog = inject(MatDialog);
    protected keycloakAuth = inject(KeycloakAuthService);

    /** Étape de la connexion Keycloak : seul l'échec affiche un message, les autres n'affichent qu'un indicateur de chargement */
    protected readonly etatKeycloak = signal<'redirection' | 'chargement' | 'echec'>('redirection');
    protected readonly messageEchec = signal('');
    private connexionKeycloakEnCours = false;
    private static readonly DELAI_REDIRECTION_ECHEC_MS = 4000;

    public titre = "";
    public codeModule: string = null; // Code module/option testé pour vérifier l'accès à cette fonction (si null = OK)
    protected lgTrad: Record<string, string>;

    @ViewChild("login") login: InputGala;
    @ViewChild("pass") pass: InputGala;

    public formLogin = this.formBuilder.group({
        'login': ['', Validators.required],
        'pass': ['', [Validators.required]]
    });

    public listeOrga: OrganismeConnexion[];
    ngOnInit() {
        super.ngOnInit();
        this.lgTrad = {
            LOGIN: this.translate.instant("LOGIN"),
            PASSWORD: this.translate.instant("PASSWORD"),
            MAIL: this.translate.instant('MAIL'),
            ENR: this.translate.instant("ENRMIN"),
            RET: this.translate.instant("RET"),
            SIGNIN: this.translate.instant("SIGNIN"),
            MDPOUBLI: this.translate.instant("MESSAGE.MDPOUBLI")
        };
        if (this.sessionUser != null) {
            this.router.navigate(["/"]).catch(e => this.util.fErreur(e));
        } else if (this.keycloakAuth.actif) {
            this.connexionKeycloak().catch(e => this.util.fErreur(e));
        }
    }

    /** Connexion par Keycloak : redirection vers Keycloak, ou échange du jeton à son retour ; tout échec renvoie vers Keycloak */
    async connexionKeycloak() {
        if (this.connexionKeycloakEnCours) { return; }
        this.connexionKeycloakEnCours = true;
        try {
            if (!this.keycloakAuth.authentifie) {
                this.etatKeycloak.set('redirection');
                await this.keycloakAuth.login();
                return;
            }
            this.clearCache();
            this.etatKeycloak.set('chargement');
            this.ouvrirSession(await this.keycloakAuth.echangerJeton());
        } catch (e) {
            await this.echecKeycloak(e);
        }
    }

    /** Affiche la cause de l'échec puis termine la session Keycloak et revient à sa page de connexion */
    private async echecKeycloak(erreur: unknown) {
        this.etatKeycloak.set('echec');
        this.messageEchec.set(this.translate.instant('AUTHKEYCLOAK.ECHEC', { libelle: this.extraireMessage(erreur) }));
        await this.util.delay(AuthComponent.DELAI_REDIRECTION_ECHEC_MS);
        if (!this.keycloakAuth.deconnecter()) {
            await this.keycloakAuth.login();
        }
    }

    private extraireMessage(erreur: unknown): string {
        const e = erreur as { error?: { message?: string }; message?: string } | null | undefined;
        return e?.error?.message ?? e?.message ?? this.translate.instant('AUTHKEYCLOAK.ERREURINCONNUE');
    }

    async connexion() {
        if (!this.formLogin.get("login").value) {
            this.formLogin.get("login").setValue(this.login.inputRef.nativeElement.value);
        }
        if (!this.formLogin.get("pass").value) {
            this.formLogin.get("pass").setValue(this.pass.inputRef.nativeElement.value);
        }

        // Suppression du cache avant connexion
        this.clearCache();
        if (this.formLogin.valid) {
            if (this.util.isOccupe()) { return false; }
            this.util.showLoader();
            try {
                const oData = { ...this.formLogin.value };
                this.wsAuth.configuration.username = oData.login.toUpperCase();
                this.wsAuth.configuration.password = oData.pass;
                const chaineOptions = Constantes.ID_APPLICATIF + ":" + environment.ws_version;
                const optJsonB64 = btoa(chaineOptions);
                const resp = await lastValueFrom(this.wsAuth.getAuthConnexionGalaclick(optJsonB64, 'response'));
                this.ouvrirSession(resp);
            } catch (e) {
                this.util.fErreur(e);
            } finally {
                this.util.hideLoader();
            }
        }
    }

    /** Ouvre la session à partir de la réponse de connexion (utilisateur + jeton Galaclick) */
    private ouvrirSession(resp: HttpResponse<unknown>) {
        sessionStorage.setItem("logged", JSON.stringify(true));
        this.util.updateToken(resp);
        const data = resp.body as Utilisateur;
        //on reinitialise les parametres pour ne pas interferer avec un uatre mode de connexion (estelevage)
        this.wsAuth.configuration.username = null;
        this.wsAuth.configuration.password = null;
        sessionStorage.setItem("user", JSON.stringify(data));
        this.monitoServ.monitorUser(data);
        //test mis en place en urgence
        const idUser = data.USRID;
        this.util.superAdmin = (idUser === 2 || idUser === 209 || idUser === 247);

        // Met a jour le menu en fonction des droits
        this.util.fRefreshMenuItem();

        this.router.navigate(["/"]).catch(e => this.util.fErreur(e));
    }

    showModalOubli() {
        //Si un login est déja entré/sasiie on le récupère et on l'affiche
        const loginMdpOublie: string = (this.formLogin.get("login").value == null) ? this.login.inputRef.nativeElement.value : this.formLogin.value.login;
        this.dialog.open(MdpOublieComponent, { data: loginMdpOublie });
    }
}

